import {
  ScheduledTaskStatus,
  useFindScheduledTaskLazyQuery,
  useSoftSkillRoleLevelContentCsvFileExportMutation,
} from 'generated/graphql';
import { useRef, useState } from 'react';

const MAX_SCHEDULED_TASK_RETRIES = 10;
const SCHEDULED_TASK_RETRY_INTERVAL = 3000;

export const useGetSoftSkillContentBlob = () => {
  const scheduledTaskSubId = useRef<string | null>(null);
  const scheduledTaskRetries = useRef(0);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  const [scheduleSoftSkillContentCsvExport] =
    useSoftSkillRoleLevelContentCsvFileExportMutation({
      onError: () => {
        setLoading(false);
        setHasError(true);
      },
    });

  const [findScheduledTask] = useFindScheduledTaskLazyQuery({
    fetchPolicy: 'network-only',
    onError: () => setHasError(true),
    onCompleted: data => {
      const status = data?.FindScheduledTask?.status;
      const blob = data?.FindScheduledTask?.blob;

      if (status === ScheduledTaskStatus.Completed && blob) {
        setBlobUrl(blob.url);
        setLoading(false);
      } else if (
        status === ScheduledTaskStatus.Running ||
        status === ScheduledTaskStatus.Scheduled
      ) {
        if (scheduledTaskRetries.current > MAX_SCHEDULED_TASK_RETRIES) {
          setLoading(false);
          setHasError(true);
          return;
        }

        setTimeout(() => {
          findScheduledTask({
            variables: {
              subId: scheduledTaskSubId.current,
            },
          });
          scheduledTaskRetries.current = scheduledTaskRetries.current + 1;
        }, SCHEDULED_TASK_RETRY_INTERVAL);
      } else if (status === ScheduledTaskStatus.Failed) {
        setLoading(false);
        setHasError(true);
      } else {
        setLoading(false);
        setHasError(true);
      }
    },
  });

  const getBlobUrl = async () => {
    setLoading(true);
    const scheduledTaskResponse = await scheduleSoftSkillContentCsvExport();
    const subId =
      scheduledTaskResponse.data?.SoftSkillRoleLevelContentCsvFileExport.subId;
    if (subId) {
      scheduledTaskSubId.current = subId;
      findScheduledTask({
        variables: {
          subId: scheduledTaskSubId.current,
        },
      });
    }
  };

  const retry = () => {
    if (!hasError) {
      return;
    }

    setHasError(false);
    scheduledTaskRetries.current = 0;
    getBlobUrl();
  };

  return {
    blobUrl,
    loading,
    hasError,
    retry,
    getBlobUrl,
  };
};
